import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { 
  Calendar, Clock, Users, User, ChevronLeft, ChevronRight, 
  CreditCard, CheckCircle, AlertCircle, ArrowLeft, Wallet,
  Phone, Mail, Package, Sparkles, MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SmsOptIn, smsStepValid } from "@/components/booking/SmsOptIn";
import WaitlistForm from "@/components/kunden/WaitlistForm";
import AboBooking from "@/components/abo/AboBooking";
import PackagePromo from "@/components/booking/PackagePromo";

export const NO_REFUND_TEXT = "Bei einer Stornierung bis spätestens 24 Stunden vor Trainingsbeginn erhältst du keine Rückzahlung. Stattdessen kannst du einen Ersatztermin buchen oder erhältst ein entsprechendes Trainingsguthaben.";
const CancelTerms = ({ testId }) => (
  <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 text-sm text-left" data-testid={testId}>
    <p className="font-semibold text-white mb-1">Stornierungsbedingungen</p>
    <p className="text-[#A1A1AA]">Online-Stornierung bis 24 Stunden vor Trainingsbeginn. {NO_REFUND_TEXT}</p>
  </div>
);
import { useCardsAccess, cardHeaders, PayModeSwitch, CardVerifyBox, CardSummary } from "@/components/booking/CardPayment";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Training types with icons
const TRAINING_ICONS = {
  einzeltraining_kind: User,
  einzeltraining_erwachsen: User,
  gruppentraining_2_4: Users,
  gruppentraining_5_plus: Users,
  torwarttraining: User,
};

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [durationOptions, setDurationOptions] = useState([]);
  const [trainingLocation, setTrainingLocation] = useState(null);
  const [bookingHours, setBookingHours] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [dayHours, setDayHours] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [userPackages] = useState([]);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    trainingType: "",
    duration: 60, // Default duration
    playerCount: 1,
    playerAge: "",
    playerName: "",
    date: "",
    time: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerStreet: "",
    customerZipCode: "",
    customerCity: "",
    message: "",
    paymentMethod: "cash",
    usePackageCredit: false,
    mobile: "",
    smsConsent: false,
  });

  const cardsAccess = useCardsAccess();
  const location = useLocation();
  const [payMode, setPayMode] = useState("regular");
  const [card, setCard] = useState(null);
  const [cardAuth, setCardAuth] = useState({ code: null, session: null });

  const applyCard = (c, auth, email) => {
    setCard(c);
    setCardAuth(auth);
    setFormData(prev => ({ ...prev, trainingType: c.training_type, duration: 60, customerEmail: email, customerName: prev.customerName || c.customer_name, usePackageCredit: false }));
  };

  const resetCard = () => {
    setCard(null);
    setCardAuth({ code: null, session: null });
    setFormData(prev => ({ ...prev, trainingType: "", duration: 60 }));
  };

  const changePayMode = (mode) => {
    setPayMode(mode);
    if (mode !== "card") resetCard();
  };

  const [aboLogin] = useState(() => new URLSearchParams(window.location.search).get("login"));
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("modus") === "abo") setPayMode("abo");
    if (q.get("gutschrift")) setFormData(prev => ({ ...prev, creditCode: q.get("gutschrift").toUpperCase() }));
    if (q.get("login")) window.history.replaceState(null, "", "/buchen?modus=abo");
  }, []);

  // Coming from /meine-karte with an active card session
  useEffect(() => {
    const st = location.state;
    if (!st?.cardSession) return;
    fetch(`${API_URL}/api/cards/me`, { headers: cardHeaders({ "X-Card-Session": st.cardSession }) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setPayMode("card"); applyCard(d.card, { code: null, session: st.cardSession }, st.cardEmail); } });
  }, [location.state]);

  // Fetch training types and packages on mount
  useEffect(() => {
    fetchTrainingTypes();
  }, []);

  // Fetch available slots when date or duration changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate, formData.duration);
    }
  }, [selectedDate, formData.duration, formData.trainingType]);

  // Days on which the chosen training is not bookable at all (weekend lock, holidays, blocked days)
  const [closedDays, setClosedDays] = useState({});
  useEffect(() => {
    if (!formData.trainingType) return;
    const y = currentMonth.getFullYear(), m = currentMonth.getMonth();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    fetch(`${API_URL}/api/booking-rules/public/days?start=${iso(new Date(y, m, 1))}&end=${iso(new Date(y, m + 1, 0))}&training_type=${formData.trainingType}`)
      .then(r => r.ok ? r.json() : { closed: {} })
      .then(d => setClosedDays(d.closed || {}))
      .catch(() => setClosedDays({}));
  }, [currentMonth, formData.trainingType]);

  // Check for user packages when email is entered
  const fetchTrainingTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/training-types`);
      const data = await res.json();
      setTrainingTypes(data.training_types || []);
      setPackages(data.packages || []);
      setDurationOptions(data.duration_options || [{ duration: 60, label: "60 Minuten", multiplier: 1.0 }]);
      setTrainingLocation(data.training_location || null);
      setBookingHours(data.booking_hours || null);
    } catch (err) {
      console.error("Failed to fetch training types:", err);
    }
  };

  const fetchAvailableSlots = async (date, duration = 60) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/available-slots?date=${date}&duration=${duration}&training_type=${formData.trainingType}`);
      const data = await res.json();
      setAvailableSlots(data.available_slots || []);
      setDayHours(data.blocked ? (data.reason || "An diesem Tag nicht buchbar") : data.booking_hours || "");
    } catch (err) {
      console.error("Failed to fetch slots:", err);
      setAvailableSlots([]);
    }
    setLoading(false);
  };


  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/book`, {
        method: "POST",
        headers: cardHeaders(),
        body: JSON.stringify({
          date: formData.date,
          time: formData.time,
          duration: formData.duration,
          training_type: formData.trainingType,
          player_count: parseInt(formData.playerCount),
          player_age: formData.playerAge ? parseInt(formData.playerAge) : null,
          player_name: formData.playerName.trim() || null,
          customer: {
            name: formData.customerName,
            email: formData.customerEmail,
            phone: formData.customerPhone,
            address: {
              street: formData.customerStreet,
              zip_code: formData.customerZipCode,
              city: formData.customerCity,
            },
          },
          message: formData.message,
          payment_method: card ? "training_card" : formData.usePackageCredit ? "credit" : formData.paymentMethod,
          card_code: card ? cardAuth.code : null,
          card_session: card ? cardAuth.session : null,
          use_package_credit: formData.usePackageCredit,
          credit_code: formData.creditCode || null,
          mobile: formData.mobile.trim() || null,
          sms_consent: formData.smsConsent,
        }),
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setBookingSuccess(data);
        setStep(5);
        toast.success("Buchung erfolgreich!");
      } else {
        toast.error(data.detail || "Buchung fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
    setLoading(false);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for alignment
    const startDay = firstDay.getDay() || 7; // Monday = 1
    for (let i = 1; i < startDay; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 56); // 8 weeks
    return date >= today && date <= maxDate;
  };

  const formatDate = (date) => {
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse date string without timezone conversion
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Format date for display: "Dienstag, 13.10.2026"
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const date = parseLocalDate(dateStr);
    const weekday = date.toLocaleDateString("de-DE", { weekday: "long" });
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    // Capitalize first letter of weekday
    const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${capitalizedWeekday}, ${day}.${month}.${year}`;
  };

  const selectedTraining = trainingTypes.find(t => t.id === formData.trainingType);
  const durationMultiplier = durationOptions.find(d => d.duration === formData.duration)?.multiplier || 1.0;
  const totalPrice = selectedTraining 
    ? Math.round((selectedTraining.per_player 
        ? selectedTraining.base_price * formData.playerCount * durationMultiplier
        : selectedTraining.base_price * durationMultiplier))
    : 0;
  const [creditInfo, setCreditInfo] = useState(null);
  const creditCode = !card && !formData.usePackageCredit ? (formData.creditCode || "").trim() : "";
  useEffect(() => {
    if (step !== 4 || !creditCode) { setCreditInfo(null); return; }
    fetch(`${API_URL}/api/kunden/credits/check`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code: creditCode, email: formData.customerEmail }) })
      .then(async (r) => { const d = await r.json().catch(() => ({})); setCreditInfo(r.ok ? d : { error: d.detail || "Gutschrift konnte nicht geprüft werden" }); })
      .catch(() => setCreditInfo(null));
  }, [step, creditCode, formData.customerEmail]);
  const unitMisfit = creditInfo?.kind === "unit" && (creditInfo.training_type !== formData.trainingType || formData.duration > creditInfo.duration || (formData.playerCount || 1) > (creditInfo.player_count || 1));
  const creditAmount = creditInfo?.kind === "unit" ? (unitMisfit ? 0 : totalPrice) : creditInfo?.amount ? Math.min(creditInfo.amount, totalPrice) : 0;
  const payPrice = Math.round((totalPrice - creditAmount) * 100) / 100;
  return (
    <>
      <Helmet>
        <title>Online Terminbuchung | Aktas Personal Fussballtraining</title>
        <meta name="description" content="Buche dein Personal Training online - Wähle Datum, Zeit und Trainingsart." />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-[#c1ff72] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Zurück zur Startseite</span>
            </Link>
            <h1 className="font-['Bebas_Neue'] text-xl sm:text-2xl tracking-wide">
              ONLINE <span className="text-[#c1ff72]">BUCHEN</span>
            </h1>
            <div className="w-20" />
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          
          {/* Progress Stepper */}
          <div className="mb-8" data-testid="booking-stepper">
            <div className="flex items-center justify-between max-w-md mx-auto">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s ? "bg-[#c1ff72] text-[#0A0A0A]" : "bg-[#1A1A1A] text-[#71717A]"
                  }`}>
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </div>
                  {s < 4 && (
                    <div className={`w-10 sm:w-24 h-1 mx-1 sm:mx-2 ${
                      step > s ? "bg-[#c1ff72]" : "bg-[#1A1A1A]"
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between max-w-md mx-auto mt-2 text-xs text-[#71717A]">
              <span>Training</span>
              <span>Termin</span>
              <span>Daten</span>
              <span>Zahlung</span>
            </div>
          </div>

          {/* Step 1: Training Type Selection */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-training">
              <div className="text-center mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  WÄHLE DEIN <span className="text-[#c1ff72]">TRAINING</span>
                </h2>
                <p className="text-[#A1A1AA]">Welche Art von Training möchtest du buchen?</p>
              </div>

              <div className="space-y-4">
                  {cardsAccess.testMode && (
                    <p className="text-xs text-blue-400" data-testid="card-test-mode-hint">TESTMODUS: Trainingskarten sind nur für Admins sichtbar.</p>
                  )}
                  <PayModeSwitch value={payMode} onChange={changePayMode} showCard={cardsAccess.enabled} />
                  {payMode === "card" && (card
                    ? <CardSummary card={card} onReset={cardAuth.session ? null : resetCard} />
                    : <CardVerifyBox onVerified={(c, code, email) => applyCard(c, { code, session: null }, email)} />)}
                  {payMode === "abo" && <AboBooking loginToken={aboLogin} />}
                </div>

              {payMode === "regular" && (<>
              <div className="grid gap-4">
                {trainingTypes.map((training) => {
                  const Icon = TRAINING_ICONS[training.id] || User;
                  return (
                    <button
                      key={training.id}
                      onClick={() => setFormData(prev => ({ ...prev, trainingType: training.id }))}
                      className={`p-4 sm:p-6 rounded-lg border transition-all text-left ${
                        formData.trainingType === training.id
                          ? "bg-[#c1ff72]/10 border-[#c1ff72]"
                          : "bg-[#1A1A1A] border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`training-option-${training.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            formData.trainingType === training.id ? "bg-[#c1ff72]/20" : "bg-white/5"
                          }`}>
                            <Icon className={`w-6 h-6 ${
                              formData.trainingType === training.id ? "text-[#c1ff72]" : "text-white"
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{training.name}</h3>
                            <p className="text-sm text-[#71717A]">
                              ab {training.base_duration || 60} Min {training.per_player ? `• ${training.min_players}-${training.max_players} Spieler` : "• 1:1 Training"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-['Bebas_Neue'] text-2xl text-[#c1ff72]">{training.base_price}€</span>
                          <p className="text-xs text-[#71717A]">{training.per_player ? "pro Spieler/60 Min" : "pro 60 Min"}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Duration Selection */}
              {formData.trainingType && durationOptions.length > 0 && (
                <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
                  <Label className="text-white mb-3 block flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#c1ff72]" />
                    Trainingsdauer wählen
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {durationOptions.map((option) => {
                      const selectedTrainingData = trainingTypes.find(t => t.id === formData.trainingType);
                      const basePrice = selectedTrainingData?.base_price || 0;
                      const price = selectedTrainingData?.per_player 
                        ? basePrice * formData.playerCount * option.multiplier
                        : basePrice * option.multiplier;
                      
                      return (
                        <button
                          key={option.duration}
                          onClick={() => setFormData(prev => ({ ...prev, duration: option.duration }))}
                          className={`p-3 rounded-lg border transition-all text-center ${
                            formData.duration === option.duration
                              ? "bg-[#c1ff72]/20 border-[#c1ff72]"
                              : "bg-[#0A0A0A] border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`duration-option-${option.duration}`}
                        >
                          <div className="font-['Bebas_Neue'] text-xl">{option.duration}</div>
                          <div className="text-xs text-[#71717A]">Minuten</div>
                          <div className="text-[#c1ff72] font-semibold mt-1">{Math.round(price)}€</div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#71717A] mt-3">
                    Termine sind zwischen {bookingHours?.start || 16}:00 und {bookingHours?.end || 19}:00 Uhr buchbar
                  </p>
                </div>
              )}

              {/* Player Count for Group Training */}
              {selectedTraining?.per_player && (
                <div className="bg-[#1A1A1A] p-4 rounded-lg border border-white/10">
                  <Label className="text-white mb-2 block">Anzahl Spieler</Label>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        playerCount: Math.max(selectedTraining.min_players, prev.playerCount - 1) 
                      }))}
                      disabled={formData.playerCount <= selectedTraining.min_players}
                    >
                      -
                    </Button>
                    <span className="font-['Bebas_Neue'] text-3xl text-[#c1ff72] w-16 text-center">
                      {formData.playerCount}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        playerCount: Math.min(selectedTraining.max_players, prev.playerCount + 1) 
                      }))}
                      disabled={formData.playerCount >= selectedTraining.max_players}
                    >
                      +
                    </Button>
                  </div>
                  <p className="text-sm text-[#71717A] mt-2">
                    Gesamtpreis: <span className="text-[#c1ff72] font-semibold">{totalPrice}€</span>
                  </p>
                </div>
              )}
              </>)}

              {payMode !== "abo" && <Button
                onClick={() => setStep(2)}
                disabled={!formData.trainingType || (payMode === "card" && !card)}
                className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold py-6"
                data-testid="btn-next-step1"
              >
                Weiter zur Terminauswahl
              </Button>}
            </div>
          )}

          {/* Step 2: Date & Time Selection */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-datetime">
              <div className="text-center mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  WÄHLE DEINEN <span className="text-[#c1ff72]">TERMIN</span>
                </h2>
                <p className="text-[#A1A1AA]">
                  {formData.duration} Minuten • Am Wochenende nur Gruppentraining
                </p>
              </div>

              {/* Calendar */}
              <div className="bg-[#1A1A1A] rounded-lg p-4 sm:p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <h3 className="font-semibold">
                    {currentMonth.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((day) => (
                    <div key={day} className="text-center text-xs text-[#71717A] py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentMonth).map((date, idx) => {
                    if (!date) return <div key={`empty-${idx}`} />;
                    const dateStr = formatDate(date);
                    const isSelected = dateStr === selectedDate;
                    const closedReason = closedDays[dateStr];
                    const isSelectable = isDateSelectable(date) && !closedReason;
                    const isSunday = date.getDay() === 0;
                    
                    return (
                      <button
                        key={dateStr}
                        onClick={() => {
                          setSelectedDate(dateStr);
                          setFormData(prev => ({ ...prev, date: dateStr, time: "" }));
                        }}
                        disabled={!isSelectable}
                        title={closedReason || undefined}
                        className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                          isSelected
                            ? "bg-[#c1ff72] text-[#0A0A0A]"
                            : isSelectable
                              ? isSunday
                                ? "bg-[#c1ff72]/10 hover:bg-[#c1ff72]/20 text-[#c1ff72]"
                                : "hover:bg-white/10"
                              : "text-[#3A3A3A] cursor-not-allowed"
                        }`}
                        data-testid={`calendar-day-${dateStr}`}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div className="bg-[#1A1A1A] rounded-lg p-4 sm:p-6 border border-white/10">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#c1ff72]" />
                    Verfügbare Zeiten am {parseLocalDate(selectedDate).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}
                  </h3>
                  <p className="text-xs text-[#71717A] mb-4" data-testid="day-hours">
                    Trainingsdauer: {formData.duration} Minuten{dayHours ? ` • Buchbar: ${dayHours}` : ""}
                  </p>
                  
                  {loading ? (
                    <div className="text-center py-8 text-[#71717A]">Lade verfügbare Zeiten...</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {availableSlots.filter(s => s.available).length === 0 ? (
                        <div className="col-span-full text-center py-4 text-[#71717A]">
                          Keine verfügbaren Zeiten für {formData.duration} Minuten an diesem Tag.
                        </div>
                      ) : (
                        availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => setFormData(prev => ({ ...prev, time: slot.time }))}
                            disabled={!slot.available}
                            title={!slot.available ? slot.reason : `${slot.time} - ${slot.end_time}`}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                              formData.time === slot.time
                                ? "bg-[#c1ff72] text-[#0A0A0A]"
                                : slot.available
                                  ? "bg-white/5 hover:bg-white/10 border border-white/10"
                                  : "bg-[#1A1A1A] text-[#3A3A3A] cursor-not-allowed opacity-50"
                            }`}
                            data-testid={`time-slot-${slot.time}`}
                          >
                            <div>{slot.time} - {slot.end_time}</div>
                            {!slot.available && (
                              <div className="text-[10px] mt-1 truncate">{slot.reason}</div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  {!loading && (
                    <div className="mt-4" data-testid="waitlist-step2">
                      <WaitlistForm key={selectedDate} trainingType={formData.trainingType} email={formData.customerEmail} name={formData.customerName} date={selectedDate}
                        full={availableSlots.filter(s => s.available).length === 0} />
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!formData.date || !formData.time}
                  className="flex-1 bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold"
                  data-testid="btn-next-step2"
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Customer Data */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-customer">
              <div className="text-center mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  DEINE <span className="text-[#c1ff72]">DATEN</span>
                </h2>
                <p className="text-[#A1A1AA]">Wie können wir dich erreichen?</p>
              </div>

              <div className="bg-[#1A1A1A] rounded-lg p-4 sm:p-6 border border-white/10 space-y-4">
                <div>
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    value={formData.customerName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="Max Mustermann"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-white">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.customerEmail}
                    readOnly={!!card}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                    placeholder="max@beispiel.de"
                    className={`mt-1 bg-[#0A0A0A] border-white/20 ${card ? "opacity-70 cursor-not-allowed" : ""}`}
                    data-testid="input-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-white">Telefon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                    placeholder="0174 1234567"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-phone"
                  />
                </div>

                <SmsOptIn
                  mobile={formData.mobile}
                  consent={formData.smsConsent}
                  onMobileChange={(v) => setFormData(prev => ({ ...prev, mobile: v }))}
                  onConsentChange={(v) => setFormData(prev => ({ ...prev, smsConsent: v }))}
                />

                {/* Address Section for Invoice */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <p className="text-sm text-[#A1A1AA] mb-3">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Anschrift für die Rechnung
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="street" className="text-white">Straße & Hausnummer *</Label>
                      <Input
                        id="street"
                        value={formData.customerStreet || ""}
                        onChange={(e) => setFormData(prev => ({ ...prev, customerStreet: e.target.value }))}
                        placeholder="Musterstraße 1"
                        className="mt-1 bg-[#0A0A0A] border-white/20"
                        data-testid="input-street"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="zipCode" className="text-white">PLZ *</Label>
                        <Input
                          id="zipCode"
                          value={formData.customerZipCode || ""}
                          onChange={(e) => setFormData(prev => ({ ...prev, customerZipCode: e.target.value }))}
                          placeholder="12345"
                          className="mt-1 bg-[#0A0A0A] border-white/20"
                          maxLength={5}
                          data-testid="input-zipcode"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label htmlFor="city" className="text-white">Stadt *</Label>
                        <Input
                          id="city"
                          value={formData.customerCity || ""}
                          onChange={(e) => setFormData(prev => ({ ...prev, customerCity: e.target.value }))}
                          placeholder="Musterstadt"
                          className="mt-1 bg-[#0A0A0A] border-white/20"
                          data-testid="input-city"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="playerName" className="text-white">Name des Spielers (optional, falls abweichend)</Label>
                  <Input
                    id="playerName"
                    value={formData.playerName}
                    onChange={(e) => setFormData(prev => ({ ...prev, playerName: e.target.value }))}
                    placeholder="z.B. Leo Mustermann"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    maxLength={120}
                    data-testid="input-player-name"
                  />
                </div>

                <div>
                  <Label htmlFor="age" className="text-white">Spieleralter (optional)</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.playerAge}
                    onChange={(e) => setFormData(prev => ({ ...prev, playerAge: e.target.value }))}
                    placeholder="z.B. 12"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-age"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Nachricht (optional)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Besondere Wünsche oder Anmerkungen..."
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    rows={3}
                    data-testid="input-message"
                  />
                </div>
                {!card && !formData.usePackageCredit && <div>
                  <Label htmlFor="credit-code" className="text-white">Gutschrift- oder Ersatztermin-Nummer (optional)</Label>
                  <Input id="credit-code" value={formData.creditCode || ""} onChange={(e) => setFormData(prev => ({ ...prev, creditCode: e.target.value.toUpperCase() }))}
                    placeholder="z. B. GS-1A2B3C4D" className="mt-1 bg-[#0A0A0A] border-white/20" data-testid="input-credit-code" />
                  <p className="text-xs text-[#71717A] mt-1">Gutschriften aus einer Absage findest du unter „Meine Termine“. Sie wird mit dem Preis verrechnet, die E-Mail muss übereinstimmen. Ist die Gutschrift höher als der Preis, verfällt der Restbetrag.</p>
                </div>}
              </div>

              {/* Package Credit Hint */}
              {!card && userPackages.length > 0 && (
                <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 rounded-lg p-4 flex items-start gap-3">
                  <Wallet className="w-5 h-5 text-[#c1ff72] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#c1ff72]">Guthaben gefunden!</p>
                    <p className="text-sm text-[#A1A1AA]">
                      Du hast {userPackages[0].remaining_credits} Einheiten auf deiner {userPackages[0].package_name}. 
                      Du kannst diese im nächsten Schritt einlösen.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!formData.customerName || !formData.customerEmail || !formData.customerPhone || !formData.customerStreet || !formData.customerZipCode || !formData.customerCity || !smsStepValid(formData.mobile, formData.smsConsent)}
                  className="flex-1 bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold"
                  data-testid="btn-next-step3"
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Payment Method */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-payment">
              <div className="text-center mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  <span className="text-[#c1ff72]">ZAHLUNG</span> & ZUSAMMENFASSUNG
                </h2>
                <p className="text-[#A1A1AA]">Überprüfe deine Buchung und wähle die Zahlungsart</p>
              </div>

              {/* Summary */}
              <div className="bg-[#1A1A1A] rounded-lg p-4 sm:p-6 border border-white/10">
                <h3 className="font-semibold mb-4">Deine Buchung</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Training</span>
                    <span>{selectedTraining?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Dauer</span>
                    <span>{formData.duration} Minuten</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Datum</span>
                    <span>{parseLocalDate(formData.date).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Uhrzeit</span>
                    <span>{formData.time} - {availableSlots.find(s => s.time === formData.time)?.end_time || ''} Uhr</span>
                  </div>
                  {selectedTraining?.per_player && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Spieler</span>
                      <span>{formData.playerCount}</span>
                    </div>
                  )}
                  {trainingLocation && (
                    <div className="flex justify-between">
                      <span className="text-[#71717A]">Trainingsort</span>
                      <span className="text-right">{trainingLocation.full}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 my-3" />
                  {card ? (
                    <>
                      <div className="flex justify-between font-semibold text-lg">
                        <span>Bezahlung</span>
                        <span className="text-[#c1ff72]" data-testid="summary-card-payment">Bezahlung mit Trainingskarte</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#71717A]">Verbleibende Einheiten</span>
                        <span data-testid="summary-card-remaining">{card.credits_remaining - 1} (nach dieser Anfrage)</span>
                      </div>
                    </>
                  ) : (<>
                  {creditInfo?.error && <p className="text-xs text-red-300" data-testid="summary-credit-error">{creditInfo.error}</p>}
                  {unitMisfit && <p className="text-xs text-red-300" data-testid="summary-credit-error">Dieser Ersatztermin gilt nur für {creditInfo.training_name} ({creditInfo.duration} Min.).</p>}
                  {creditAmount > 0 && <>
                    <div className="flex justify-between"><span className="text-[#71717A]">Preis</span><span>{totalPrice}€</span></div>
                    <div className="flex justify-between" data-testid="summary-credit"><span className="text-[#71717A]">{creditInfo?.kind === "unit" ? "Ersatztermin" : "Gutschrift"} {creditCode.toUpperCase()}</span><span className="text-[#c1ff72]">−{creditAmount.toFixed(2).replace(".", ",")}€</span></div>
                    {creditInfo.kind !== "unit" && creditInfo.amount > totalPrice && <p className="text-xs text-yellow-300" data-testid="summary-credit-forfeit">Die Gutschrift ({creditInfo.amount.toFixed(2).replace(".", ",")}€) ist höher als der Preis. Der Restbetrag verfällt.</p>}
                  </>}
                  <div className="flex justify-between font-semibold text-lg">
                    <span>{creditAmount > 0 ? "Zu zahlen" : "Gesamtpreis"}</span>
                    <span className="text-[#c1ff72]" data-testid="summary-total">{creditAmount > 0 ? `${payPrice.toFixed(2).replace(".", ",")}€` : `${totalPrice}€`}</span>
                  </div>
                  </>)}
                </div>
              </div>

              {card && (
                <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/40 rounded-lg p-4 text-sm" data-testid="card-payment-info">
                  <p className="font-semibold text-[#c1ff72]">Keine weitere Zahlung nötig</p>
                  <p className="text-[#A1A1AA] mt-1">
                    Mit deiner Anfrage wird 1 Einheit deiner {card.package_name} reserviert. Abgebucht wird sie erst, wenn der Trainer den Termin bestätigt. Bei einer Absage wird sie wieder freigegeben.
                  </p>
                </div>
              )}

              {/* Package Credit Option */}
              {!card && userPackages.length > 0 && (
                <div 
                  className={`rounded-lg p-4 border cursor-pointer transition-all ${
                    formData.usePackageCredit 
                      ? "bg-[#c1ff72]/10 border-[#c1ff72]" 
                      : "bg-[#1A1A1A] border-white/10 hover:border-white/30"
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, usePackageCredit: !prev.usePackageCredit }))}
                  data-testid="option-use-credit"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      formData.usePackageCredit ? "border-[#c1ff72] bg-[#c1ff72]" : "border-white/30"
                    }`}>
                      {formData.usePackageCredit && <CheckCircle className="w-4 h-4 text-[#0A0A0A]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-[#c1ff72]" />
                        <span className="font-semibold">Guthaben einlösen</span>
                      </div>
                      <p className="text-sm text-[#71717A]">
                        {userPackages[0].remaining_credits} Einheiten verfügbar auf deiner {userPackages[0].package_name}
                      </p>
                    </div>
                    {formData.usePackageCredit && (
                      <span className="text-[#c1ff72] font-semibold">0€</span>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Methods */}
              <CancelTerms testId="step4-cancel-terms" />
              {creditAmount > 0 && payPrice === 0 && (
                <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/40 rounded-lg p-4 text-sm" data-testid="credit-no-payment">
                  <p className="font-semibold text-[#c1ff72]">Keine Zahlung nötig</p>
                  <p className="text-[#A1A1AA] mt-1">Deine Gutschrift deckt den vollen Preis.</p>
                </div>
              )}
              {!card && !formData.usePackageCredit && !(creditAmount > 0 && payPrice === 0) && (
                <div className="space-y-3">
                  <Label className="text-white">Zahlungsart wählen</Label>
                  <RadioGroup 
                    value={formData.paymentMethod} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, paymentMethod: v }))}
                    className="space-y-3"
                  >
                    <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.paymentMethod === "paypal" ? "bg-[#0070ba]/10 border-[#0070ba]" : "bg-[#1A1A1A] border-white/10"
                    }`}>
                      <RadioGroupItem value="paypal" id="paypal" data-testid="radio-paypal" />
                      <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                        <span className="font-semibold">PayPal</span>
                        <p className="text-sm text-[#71717A]">Sofort online bezahlen</p>
                      </Label>
                    </div>
                    
                    <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.paymentMethod === "bank_transfer" ? "bg-[#c1ff72]/10 border-[#c1ff72]" : "bg-[#1A1A1A] border-white/10"
                    }`}>
                      <RadioGroupItem value="bank_transfer" id="bank_transfer" data-testid="radio-bank" />
                      <Label htmlFor="bank_transfer" className="flex-1 cursor-pointer">
                        <span className="font-semibold">Banküberweisung (Vorkasse)</span>
                        <p className="text-sm text-[#71717A]">Per Überweisung vor dem Training</p>
                      </Label>
                    </div>
                    
                    <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.paymentMethod === "cash" ? "bg-[#c1ff72]/10 border-[#c1ff72]" : "bg-[#1A1A1A] border-white/10"
                    }`}>
                      <RadioGroupItem value="cash" id="cash" data-testid="radio-cash" />
                      <Label htmlFor="cash" className="flex-1 cursor-pointer">
                        <span className="font-semibold">Barzahlung</span>
                        <p className="text-sm text-[#71717A]">Vor Ort beim Training bezahlen</p>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Info Notice */}
              <div className="bg-[#422006]/50 border border-[#f97316]/30 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#f97316] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#f97316]/80">
                  <strong>Hinweis:</strong> Deine Buchung muss vom Trainer bestätigt werden. 
                  Du erhältst eine E-Mail, sobald der Termin bestätigt wurde.
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold"
                  data-testid="btn-submit"
                >
                  {loading ? "Wird gebucht..." : "Jetzt buchen"}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && bookingSuccess && (
            <div className="text-center space-y-6" data-testid="step-success">
              <CancelTerms testId="success-cancel-terms" />
              <div className="w-20 h-20 bg-[#f59e0b]/10 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-10 h-10 text-[#f59e0b]" />
              </div>
              
              <div>
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  ANFRAGE <span className="text-[#f59e0b]">ÜBERMITTELT!</span>
                </h2>
                <p className="text-[#A1A1AA] max-w-md mx-auto">
                  Deine Buchungsanfrage wurde erfolgreich übermittelt und wartet auf die Bestätigung durch den Trainer.
                </p>
              </div>

              {/* Status Notice */}
              <div className="bg-[#422006]/50 border border-[#f59e0b]/30 rounded-lg p-4 sm:p-5">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-[#f59e0b] flex-shrink-0" />
                  <span className="font-semibold text-[#f59e0b]">Status: Ausstehend</span>
                </div>
                <p className="text-sm text-[#f59e0b]/90 leading-relaxed">
                  {bookingSuccess.card
                    ? "Dein Termin ist noch nicht verbindlich bestätigt. Eine Einheit deiner Trainingskarte ist reserviert und wird erst bei Bestätigung durch den Trainer abgebucht."
                    : "Dein Termin ist noch nicht verbindlich bestätigt. Sobald der Trainer deine Anfrage angenommen hat, erhältst du eine Terminbestätigung und deine Rechnung per E-Mail."}
                </p>
              </div>

              <div className="bg-[#1A1A1A] rounded-lg p-5 sm:p-6 border border-white/10 text-left">
                <div className="flex items-center gap-2 mb-5">
                  <Calendar className="w-5 h-5 text-[#c1ff72]" />
                  <h3 className="font-semibold text-white">Angefragte Termindetails</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Anfrage-ID</span>
                    <span className="font-mono text-[#c1ff72] text-right break-all">{bookingSuccess.appointment_id}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Training</span>
                    <span className="text-white text-right">{bookingSuccess.appointment?.training}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Dauer</span>
                    <span className="text-white">{bookingSuccess.appointment?.duration || formData.duration} Minuten</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Datum</span>
                    <span className="text-white">{formatDisplayDate(bookingSuccess.appointment?.date)}</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Uhrzeit</span>
                    <span className="text-white">{bookingSuccess.appointment?.time} – {bookingSuccess.appointment?.end_time} Uhr</span>
                  </div>
                  <div className="flex justify-between items-start gap-4 py-1">
                    <span className="text-[#A1A1AA]">Trainingsort</span>
                    <span className="text-white text-right">{bookingSuccess.appointment?.training_location || trainingLocation?.full}</span>
                  </div>
                  {bookingSuccess.card ? (
                    <>
                      <div className="flex justify-between items-start gap-4 py-1 pt-2 border-t border-white/10">
                        <span className="text-[#A1A1AA]">Zahlung</span>
                        <span className="text-[#c1ff72] font-bold text-right" data-testid="success-card-payment">Bezahlung mit Trainingskarte</span>
                      </div>
                      <div className="flex justify-between items-start gap-4 py-1">
                        <span className="text-[#A1A1AA]">Verbleibende Einheiten</span>
                        <span className="text-white" data-testid="success-card-remaining">{bookingSuccess.card.credits_remaining} von {bookingSuccess.card.credits_total}</span>
                      </div>
                    </>
                  ) : (
                  <div className="flex justify-between items-start gap-4 py-1 pt-2 border-t border-white/10">
                    <span className="text-[#A1A1AA]">Preis</span>
                    <span className="text-[#c1ff72] font-bold text-base">{bookingSuccess.appointment?.total_price} €</span>
                  </div>
                  )}
                  <div className="flex justify-between items-start gap-4 py-1 pt-2 border-t border-white/10">
                    <span className="text-[#A1A1AA]">Buchungsstatus</span>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1.5 text-[#f59e0b] font-semibold">
                        <Clock className="w-4 h-4" />
                        Ausstehend
                      </span>
                      <p className="text-xs text-[#71717A] mt-0.5">Wartet auf Bestätigung</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info about next steps */}
              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-5 sm:p-6 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-[#c1ff72]" />
                  <h3 className="font-semibold text-white">Wie geht es weiter?</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-[#c1ff72]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#c1ff72] text-xs font-bold">1</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Buchungsanfrage eingegangen</p>
                      <p className="text-[#A1A1AA] text-xs mt-0.5">Du erhältst eine Eingangsbestätigung per E-Mail.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-[#c1ff72]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#c1ff72] text-xs font-bold">2</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Prüfung durch den Trainer</p>
                      <p className="text-[#A1A1AA] text-xs mt-0.5">Dein gewünschter Termin wird geprüft.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-[#c1ff72]/10 rounded-full flex items-center justify-center">
                      <span className="text-[#c1ff72] text-xs font-bold">3</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Terminbestätigung</p>
                      <p className="text-[#A1A1AA] text-xs mt-0.5">
                        {bookingSuccess.card
                          ? "Sobald dein Termin bestätigt wurde, erhältst du eine E-Mail mit deiner verbindlichen Terminbestätigung und deinem verbleibenden Guthaben. Es entsteht keine neue Rechnung."
                          : "Sobald dein Termin bestätigt wurde, erhältst du eine weitere E-Mail mit deiner verbindlichen Terminbestätigung, der Rechnung als PDF und den Zahlungsinformationen."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/" className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                    Zur Startseite
                  </Button>
                </Link>
                <Link to="/buchen" className="flex-1">
                  <Button 
                    className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold"
                    onClick={() => {
                      setStep(1);
                      resetCard();
                      setPayMode("regular");
                      setFormData({
                        trainingType: "",
                        duration: 60,
                        playerCount: 1,
                        playerAge: "",
                        date: "",
                        time: "",
                        customerName: "",
                        customerEmail: "",
                        customerPhone: "",
                        customerStreet: "",
                        customerZipCode: "",
                        customerCity: "",
                        message: "",
                        paymentMethod: "cash",
                        usePackageCredit: false,
                        mobile: "",
                        smsConsent: false,
                      });
                      setSelectedDate(null);
                      setBookingSuccess(null);
                    }}
                  >
                    Weitere Buchung
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Package Purchase CTA */}
          {step < 5 && <PackagePromo />}
        </main>
      </div>
    </>
  );
}
